import React, { useState, useMemo, useEffect } from 'react';
import { formatKoreanPhone } from '../utils/phone';
import SortableHeader from './SortableHeader';
import StatusCell from './StatusCell';
import BookingDetailPopover from './BookingDetailPopover';
import { sortBookings, filterBookings } from './bookingTableHelpers';

// Excel-style bookings table.
// Search (debounced 250ms), sort (via SortableHeader), pagination (50/100/all).
// Presentational: fetch and actions are owned by parent BookingManager.

const PAGE_SIZES = [50, 100, 0]; // 0 = show all

const COLUMNS = [
  { key: 'date', label: '날짜', width: 100, sortable: true },
  { key: 'time', label: '시간', width: 60, sortable: true },
  { key: 'customerName', label: '고객명', width: 100, sortable: true },
  { key: 'customerPhone', label: '전화', width: 130, sortable: true },
  { key: 'vehicleType', label: '차종', width: 100, sortable: true },
  { key: 'services', label: '서비스', width: 200, sortable: false },
  { key: 'amount', label: '금액', width: 100, sortable: true },
  { key: 'status', label: '상태', width: 90, sortable: true },
  { key: '__detail', label: '', width: 40, sortable: false },
];

export default function BookingsTable({
  bookings,
  services,
  vehicleName,
  renderService,
  updating,
  deleting,
  onStatusChange,
  onDelete,
}) {
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  // Debounce search input (250ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever filter, sort, or dataset size changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortKey, sortDir, bookings.length]);

  const filtered = useMemo(
    () => filterBookings(bookings, debouncedSearch),
    [bookings, debouncedSearch]
  );

  const sorted = useMemo(
    () => sortBookings(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir]
  );

  const total = sorted.length;
  const effectivePageSize = pageSize === 0 ? total : pageSize;
  const totalPages = effectivePageSize > 0 ? Math.max(1, Math.ceil(total / effectivePageSize)) : 1;
  const startIdx = (page - 1) * effectivePageSize;
  const visible = pageSize === 0 ? sorted : sorted.slice(startIdx, startIdx + effectivePageSize);

  const onHeaderClick = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="🔍 고객명·전화 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '1 1 240px', maxWidth: 320, padding: '8px 12px',
            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            background: 'var(--surface)', color: 'var(--text)',
          }}
        />
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{
            padding: '8px 12px', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', background: 'var(--surface)', color: 'var(--text)',
          }}
        >
          {PAGE_SIZES.map(n => (
            <option key={n} value={n}>{n === 0 ? '전체' : `${n}건/페이지`}</option>
          ))}
        </select>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {total === 0
            ? '예약 없음'
            : pageSize === 0
              ? `총 ${total}건`
              : `${startIdx + 1}~${Math.min(startIdx + effectivePageSize, total)} / ${total}건`}
        </span>
      </div>

      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: 920 }}>
          <caption style={{
            position: 'absolute', width: 1, height: 1, overflow: 'hidden',
            clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
          }}>예약 목록</caption>
          <colgroup>
            {COLUMNS.map(col => <col key={col.key} style={{ width: col.width }} />)}
          </colgroup>
          <thead>
            <tr>
              {COLUMNS.map(col => {
                const headerStyle = {
                  width: col.width,
                  position: 'sticky',
                  top: 0,
                  background: 'var(--surface)',
                  borderBottom: '2px solid var(--border)',
                  zIndex: 1,
                };
                if (col.sortable) {
                  return (
                    <SortableHeader
                      key={col.key}
                      label={col.label}
                      sortKey={col.key}
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={onHeaderClick}
                      style={headerStyle}
                    />
                  );
                }
                return (
                  <th key={col.key} style={{
                    ...headerStyle,
                    padding: '8px 10px',
                    textAlign: 'left',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map(b => {
              const busy = updating === b.bookingId || deleting === b.bookingId;
              return (
                <tr key={b.bookingId} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={cellStyle}>{b.date}</td>
                  <td style={cellStyle}>{b.time}</td>
                  <td style={cellStyle}>
                    {b.customerName}
                    {b.note && <span title={b.note} style={{ marginLeft: 4 }}>✏️</span>}
                  </td>
                  <td style={cellStyle}>
                    <a href={`tel:${b.customerPhone}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                      {formatKoreanPhone(b.customerPhone)}
                    </a>
                  </td>
                  <td style={cellStyle}>{vehicleName(b.vehicleType)}</td>
                  <td style={{ ...cellStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={(b.services || []).map(renderService).join(', ')}>
                    {(b.services || []).map(renderService).join(', ')}
                  </td>
                  <td style={{ ...cellStyle, fontWeight: 600, color: 'var(--gold)' }}>
                    {b.amount ? `₩${b.amount.toLocaleString()}` : '문의'}
                  </td>
                  <td style={cellStyle}>
                    <StatusCell
                      booking={b}
                      busy={busy}
                      onStatusChange={onStatusChange}
                      onDelete={onDelete}
                    />
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <BookingDetailPopover booking={b} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageSize !== 0 && totalPages > 1 && (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 12, alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle}>이전</button>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '0 8px' }}>
            {page} / {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle}>다음</button>
        </div>
      )}
    </div>
  );
}

const cellStyle = {
  padding: '10px 8px',
  verticalAlign: 'middle',
};

const pageBtnStyle = {
  padding: '6px 12px', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', background: 'var(--surface)',
  color: 'var(--text)', cursor: 'pointer', fontSize: '0.85rem',
};
